import type { ComponentType, ReactNode } from 'react';
import { Tooltip, cn } from '@skupreme/shared/ui';

import { AccountMark } from './AccountMark';
import { ACCOUNT_LEVEL_LABEL } from './AccountLevelOption';
import type { OrgRowProps } from './OrganizationSwitcher';
import type { OrgGroup } from './organizations';

export const REQUEST_ACCESS_TIP = 'Please request access from admin';

/* The tree line from the account's mark down to each division: a grey rule
   (the theme's `rule`) through the middle of the header's mark, a tick into
   every division row, stopping at the last one's middle. The first segment
   reaches up through the gap and the header's padding to its mark; the
   others bridge the gap above them. */
const LINE = 'sk:pointer-events-none sk:absolute sk:border-rule sk:left-5';

function TreeBranch({
  first,
  last,
  children,
}: {
  first: boolean;
  last: boolean;
  children: ReactNode;
}) {
  return (
    <div className="sk:relative" data-tree-branch={last ? 'last' : 'mid'}>
      {children}
      <span
        aria-hidden
        className={cn(
          LINE,
          'sk:border-l',
          first ? 'sk:-top-3' : 'sk:-top-1',
          last ? 'sk:bottom-1/2' : 'sk:bottom-0',
        )}
      />
      <span
        aria-hidden
        className={cn(LINE, 'sk:top-1/2 sk:w-2.5 sk:border-t')}
      />
    </div>
  );
}

/** One group of the switcher's list (handed the switcher's row as `Row`).
    A standalone org is a plain row. An account is its own row, picked for
    the account level only when the server allows it (else a plain header,
    which says to ask an admin where the account level is switched on), with
    its divisions indented under it on a tree line. */
export function AccountGroupRows({
  group,
  Row,
  currentOrg,
  accountSelected,
  onPickAccount,
  onPickDivision,
}: {
  group: OrgGroup;
  Row: ComponentType<OrgRowProps>;
  currentOrg?: string;
  /** The account level is the one in force, in this group's account. */
  accountSelected: boolean;
  onPickAccount: (account: NonNullable<OrgGroup['account']>) => void;
  onPickDivision: (id: string) => void;
}) {
  const { account, divisions } = group;
  const divisionRow = (org: OrgGroup['divisions'][number]) => (
    <Row
      key={org._id}
      org={org}
      indent={!!account}
      label={org.company_name || 'No Name'}
      selected={org._id === currentOrg && !accountSelected}
      onPress={() => onPickDivision(org._id)}
    />
  );
  if (!account) return divisions.map(divisionRow);

  const header = (
    <Row
      icon={<AccountMark logoUrl={account.logoUrl} />}
      label={account.name}
      secondary={account.accountLevel ? ACCOUNT_LEVEL_LABEL : undefined}
      selected={accountSelected}
      onPress={account.accountLevel ? () => onPickAccount(account) : undefined}
    />
  );

  return (
    <fieldset
      aria-label={account.name}
      className="sk:m-0 sk:flex sk:min-w-0 sk:flex-col sk:gap-1 sk:border-0 sk:p-0"
    >
      {!account.accountLevel && account.accountViewEnabled ? (
        <Tooltip
          content={REQUEST_ACCESS_TIP}
          placement="right"
          triggerClassName="sk:w-full sk:cursor-default"
        >
          <div className="sk:w-full">{header}</div>
        </Tooltip>
      ) : (
        header
      )}
      {divisions.map((org, i) => (
        <TreeBranch
          key={org._id}
          first={i === 0}
          last={i === divisions.length - 1}
        >
          {divisionRow(org)}
        </TreeBranch>
      ))}
    </fieldset>
  );
}
